import type React from "react";

import { useFormikContext } from "formik";

import { useConfirmLoseUnsaved } from "hooks/useConfirmLoseUnsaved";

interface ConfirmLoseUnsavedFormikProps {
  message?: string;
}

/**
 * A component that blocks navigation and prompts the user with a confirmation dialog if there are unsaved changes in a Formik form. This component is intended to be rendered inside a Formik context (i.e., within a Formik render prop or a child of a <Formik> component). It uses the `useConfirmLoseUnsaved` hook under the hood, automatically wiring up Formik's `dirty` state.
 * When to use:
 * - Use <ConfirmLoseUnsavedFormik> when Formik is rendered inside your component and you want to block navigation if the form is dirty.
 * - Use the `useConfirmLoseUnsaved` hook directly if you have access to the dirty state in your component (e.g., via props or context), or if you are not using Formik.
 *
 * @example
 * import { Formik } from "formik";
 * import { ConfirmLoseUnsavedFormik } from "components/Forms/components/ConfirmLoseUnsavedFormik";
 *
 * <Formik initialValues={...} onSubmit={...}>
 *   {() => (
 *     <form>
 *       <ConfirmLoseUnsavedFormik message="You have unsaved changes. Are you sure you want to leave?" />
 *       ...
 *     </form>
 *   )}
 * </Formik>
 *
 * @param {Object} props
 * @param {string} [props.message] - The confirmation message. Defaults to "You have unsaved changes. Are you sure you want to leave?"
 *
 * @remarks
 * - This component must be rendered inside a Formik context.
 * - For non-Formik forms or if you need more control, use the `useConfirmLoseUnsaved` hook directly.
 */
export const ConfirmLoseUnsavedFormik: React.FC<
  ConfirmLoseUnsavedFormikProps
> = ({ message }) => {
  const { dirty } = useFormikContext();
  useConfirmLoseUnsaved({ when: dirty, message });
  return null;
};
